import { type Getter, type Setter, atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import type { V1BaseSpec } from "@moment/api-collab/differential/v1-base-spec";
import { Logger } from "@moment/logging";

import {
	MetadataCollabSubscriptionError,
	type MetadataConflictedError,
	type MetadataLoadError,
	MetadataMisconfiguredError,
	MetadataNotLoadedError,
	MetadataPersistError,
	MetadataTransactionProcHaltedError,
} from "./errors";
import type { MetadataStateParams } from "./metadata-state.types";

const logger = new Logger("metadata-state");

/**
 * The base spec used for the metadata store.
 * This is a simple schema with a single "metadata" table containing the document metadata.
 */
export const METADATA_BASE_SPEC: V1BaseSpec = {
	tables: { metadata: { permissions: [], columns: [{ name: "metadata", type: "BLOB" }] } },
	views: {},
};

interface MetadataStateContainer {
	metadata: DocumentMetadata | null;
	params: MetadataStateParams;
	error:
		| MetadataNotLoadedError
		| MetadataLoadError
		| MetadataConflictedError
		| MetadataPersistError
		| MetadataCollabSubscriptionError
		| null;
	/** AbortController for stopping collab listeners */
	abortController?: AbortController;
	/** Identifies the code path that last wrote to this container (survives minification). */
	lastWriteSource?: string;
}

/**
 * @internal INTERNAL atom holding the metadata value and params. Use $documentMetadata for reading/writing.
 */
export const $metadataStateValue = atomFamily((key: string) => {
	// IMPORTANT: MUST CAST here to work around a typing bug.
	//
	// With `strictNullChecks: false`, `null` is assignable to function types, so `atom(null)`
	// matches jotai's `atom(read: Function): Atom<T>` (read-only) overload. Casting `null` to
	// `MetadataStateContainer | null` seems to resolve the issue.
	//
	// NOTE: `atom<MetadataStateContainer | null>` DOES NOT fix the situation. You MUST cast.
	const valueAtom = atom(null as MetadataStateContainer | null);
	valueAtom.debugLabel = `metadataStateValue::${key}`;
	return valueAtom;
});

/**
 * Provides read/write access to document metadata with optional collab and persistence.
 */
export const $documentMetadata = atomFamily((key: string) => {
	const $stateAtom = atom(
		(get): DocumentMetadata | null => get($metadataStateValue(key))?.metadata ?? null,

		async (get, set, update: Partial<DocumentMetadata>) => {
			//////////////////////////////////////////////////////////////////////////////////////
			// IMPORTANT: SYNCHRONOUS ZONE. DO NOT PUT ANYTHING SLOW HERE. ///////////////////////
			const container = get($metadataStateValue(key));
			if (!container || !container.metadata) {
				logger.error("Cannot apply metadata transaction: not loaded", { key });
				const error = new MetadataNotLoadedError();
				// Set error state for tracking, but throw so the caller knows.
				set($metadataStateValue(key), {
					metadata: null,
					params: container?.params ?? { key },
					error,
					abortController: container?.abortController,
					lastWriteSource: "documentMetadata.set:notLoaded",
				});
				throw error;
			}

			const { metadata: current, params, error, abortController } = container;

			// Check if metadata is halted.
			if (error) {
				throw new MetadataTransactionProcHaltedError(error);
			}

			// Check if metadata is misconfigured (not writing to collab or disk)
			if (!params.sendToCollab && !params.onPersist) {
				throw new MetadataMisconfiguredError(
					"Failed to update metadata: persistence not configured"
				);
			}

			// Update. NEVER change params, error, or abortController.
			const newMetadata: DocumentMetadata = { ...current, ...update, updated: new Date() };
			set($metadataStateValue(key), {
				metadata: newMetadata,
				params,
				error,
				abortController,
				lastWriteSource: "documentMetadata.set:update",
			});
			// IMPORTANT: END SYNCHRONOUS ZONE. //////////////////////////////////////////////////
			//////////////////////////////////////////////////////////////////////////////////////

			// ========== ASYNC ZONE (CALLBACKS) ==========
			// Send to collab if enabled
			if (params.sendToCollab) {
				await Promise.resolve(params.sendToCollab(newMetadata)).catch((err) => {
					logger.error("Failed to send to collab", { key, err });
				});
			}

			// Persist to disk if enabled (caller handles backpressure)
			if (params.onPersist) {
				await Promise.resolve(params.onPersist(set, newMetadata)).catch((err) => {
					logger.error("Failed to persist", { key, err });

					// Set critical error - halt metadata
					const currentContainer = get($metadataStateValue(key));
					if (!currentContainer) {
						return;
					}

					const error = new MetadataPersistError(err);
					set($metadataStateValue(key), {
						...currentContainer,
						error,
						lastWriteSource: "documentMetadata.set:persistError",
					});
				});
			}
		}
	);

	$stateAtom.debugLabel = `documentMetadata::${key}`;
	return $stateAtom;
});

export type ApplyExternalDocumentMetadataInput = {
	key: string;
	metadata: DocumentMetadata;
};

/**
 * Applies external metadata changes without triggering persistence or collab callbacks.
 * Used by file watcher handling for moment.yml edits.
 */
export const $applyExternalDocumentMetadata = atom(
	null,
	(get, set, input: ApplyExternalDocumentMetadataInput) => {
		const { key, metadata } = input;
		const container = get($metadataStateValue(key));
		if (!container || !container.metadata) {
			logger.warn("Cannot apply external metadata: state not initialized", { key });
			return;
		}

		const { params, error, abortController } = container;
		set($metadataStateValue(key), {
			metadata,
			params,
			error,
			abortController,
			lastWriteSource: "applyExternalDocumentMetadata",
		});
	}
);
$applyExternalDocumentMetadata.debugLabel = "applyExternalDocumentMetadata";

/**
 * Starts the collab listeners (remote changes) for a given metadata key.
 * Called from $loadDocumentMetadata after the container is set.
 */
function startCollabListeners(
	key: string,
	abortController: AbortController,
	get: Getter,
	set: Setter
): void {
	const container = get($metadataStateValue(key));
	if (!container) {
		logger.error("Cannot start collab listeners: container not initialized", { key });
		return;
	}
	const { remoteChanges } = container.params;

	if (!remoteChanges) {
		return;
	}

	//
	// SUBSCRIBE TO REMOTE CHANGES TO METADATA.
	//

	void (async function listenForRemoteChanges() {
		try {
			logger.info("Starting remote changes listener", { key });

			const iterator = remoteChanges({ signal: abortController.signal });
			for await (const metadata of iterator) {
				if (abortController.signal.aborted) {
					break;
				}

				logger.debug("Received remote metadata", { key });

				// Get current container
				const currentContainer = get($metadataStateValue(key));
				if (!currentContainer || !currentContainer.metadata) {
					logger.warn("Cannot apply remote metadata: state not initialized", { key });
					continue;
				}
				const { params, error, abortController: ac } = currentContainer;

				// Update state while preserving params, error, and abortController.
				set($metadataStateValue(key), {
					metadata,
					params,
					error,
					abortController: ac,
					lastWriteSource: "collabRemoteChanges",
				});

				// Also persist remote changes
				if (params.onPersist) {
					await Promise.resolve(params.onPersist(set, metadata)).catch((err) => {
						logger.error("Failed to persist remote changes", { key, err });

						// Set critical error
						const errorContainer = get($metadataStateValue(key));
						if (!errorContainer) {
							return;
						}

						const error = new MetadataPersistError(err);
						set($metadataStateValue(key), {
							...errorContainer,
							error,
							lastWriteSource: "collabRemoteChanges:persistError",
						});
					});
				}
			}
		} catch (err) {
			if (abortController.signal.aborted) {
				return;
			}

			logger.error("Remote changes listener error", { key, err });

			// Set critical error.
			const errorContainer = get($metadataStateValue(key));
			if (!errorContainer) {
				return;
			}
			const error = new MetadataCollabSubscriptionError(err);
			set($metadataStateValue(key), {
				...errorContainer,
				error,
				lastWriteSource: "collabSubscriptionError",
			});
		}
	})();
}

/**
 * Provides read-only access to the metadata error (for UI consumption).
 */
export const $documentMetadataError = atomFamily((key: string) => {
	const errorAtom = atom((get) => get($metadataStateValue(key))?.error ?? null);
	errorAtom.debugLabel = `documentMetadataError::${key}`;
	return errorAtom;
});

export type LoadDocumentMetadataInput = MetadataStateParams & {
	metadata: DocumentMetadata | null;
	/** If true, load in read-only mode (no persistence required) */
	ssr?: boolean;
};

/**
 * Loads document metadata. Once called and succeeds, the metadata at `key` will be fully
 * initialized and (if configured to be writable) ready to accept updates.
 */
export const $loadDocumentMetadata = atom(null, (get, set, input: LoadDocumentMetadataInput) => {
	const { key, metadata, ssr = false, remoteChanges, sendToCollab, onPersist } = input;

	// Check if metadata is misconfigured (not in SSR mode and not writing to collab or disk)
	if (!ssr && !sendToCollab && !onPersist) {
		throw new MetadataMisconfiguredError("Failed to load metadata: persistence not configured");
	}

	// Create AbortController for managing collab listeners lifecycle
	const abortController = new AbortController();

	const params: MetadataStateParams = { key, remoteChanges, sendToCollab, onPersist };
	set($metadataStateValue(key), {
		metadata,
		params,
		error: null,
		abortController,
		lastWriteSource: "loadDocumentMetadata",
	});

	// Start collab listeners after setting the container (ensures no race condition)
	if (!ssr) {
		startCollabListeners(key, abortController, get, set);
	}
});
$loadDocumentMetadata.debugLabel = "loadDocumentMetadata";

export type SetMetadataStateErrorInput = {
	key: string;
	error: MetadataStateContainer["error"];
};

/**
 * Sets an error on the metadata state at `key`. Used by loaders when loading fails.
 */
export const $setMetadataStateError = atom(null, (get, set, input: SetMetadataStateErrorInput) => {
	const { key, error } = input;
	const existing = get($metadataStateValue(key));
	set($metadataStateValue(key), {
		metadata: existing?.metadata ?? null,
		params: existing?.params ?? { key },
		error,
		abortController: existing?.abortController,
		lastWriteSource: "setMetadataStateError",
	});
});
$setMetadataStateError.debugLabel = "setMetadataStateError";

/**
 * Unloads metadata from `key`, freeing all resources associated with it. Once successfully
 * completed, all subsequent reads and writes to the metadata at `key` will throw an error.
 */
export const $unloadDocumentMetadata = atom(null, (get, set, key: string) => {
	// Abort collab listeners before cleaning up
	const container = get($metadataStateValue(key));
	if (container?.abortController) {
		logger.info("Stopping collab listeners", { key });
		container.abortController.abort();
	}

	set($metadataStateValue(key), null);
	$metadataStateValue.remove(key);
	$documentMetadata.remove(key);
});
$unloadDocumentMetadata.debugLabel = "unloadDocumentMetadata";

/**
 * Provides read-only access to the metadata params (for testing).
 */
export const $documentMetadataConfig = atomFamily((key: string) => {
	const configAtom = atom((get) => get($metadataStateValue(key))?.params ?? null);
	configAtom.debugLabel = `documentMetadataConfig::${key}`;
	return configAtom;
});
